import React from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'

const PrivacyPolicyModal = ({ isOpen, toggle }) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>Privacy Policy (Singapore PDPA)</ModalHeader>
            <ModalBody style={styles.modalBody}>
                <div style={styles.container}>
                    <p style={styles.paragraphMeta}>
                        <strong>Last updated:</strong> 4 Dec 2025
                    </p>

                    <p style={styles.paragraph}>
                        Local Chapter (<strong>"we"</strong>, <strong>"us"</strong>, <strong>"our"</strong>) respects your privacy
                        and is committed to protecting your personal data. This Privacy Policy explains how we collect, use,
                        disclose, and protect your personal data in accordance with the{' '}
                        <strong>Singapore Personal Data Protection Act 2012 (PDPA)</strong>.
                    </p>

                    <h3 style={styles.heading}>1. Personal data we collect (minimal collection)</h3>
                    <p style={styles.paragraphSmall}>
                        We <strong>only</strong> collect the following personal data for membership:
                    </p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>
                            <strong>Name</strong>
                        </li>
                        <li style={styles.listItem}>
                            <strong>Email</strong>
                        </li>
                    </ul>
                    <p style={styles.paragraph}>We do not require NRIC/FIN or other sensitive identifiers for membership.</p>

                    <h3 style={styles.heading}>2. Why we collect your data (limited purposes only)</h3>
                    <p style={styles.paragraphSmall}>
                        We collect and use your name and email{' '}
                        <strong>only for purposes related to Local Chapter transactions</strong>, specifically to:
                    </p>
                    <ul style={styles.listSmall}>
                        <li style={styles.listItem}>Create and manage your membership profile</li>
                        <li style={styles.listItem}>
                            <strong>Issue and track loyalty points</strong> for your purchases
                        </li>
                        <li style={styles.listItem}>
                            Link your <strong>dish ratings/feedback</strong> to your membership (so we can understand performance
                            and improve)
                        </li>
                        <li style={styles.listItem}>
                            Contact you <strong>only when needed for transaction or membership administration</strong> (e.g.,
                            issues with points, account verification, support)
                        </li>
                    </ul>
                    <p style={styles.paragraph}>
                        We <strong>do not</strong> use your name or email for unrelated purposes.
                    </p>

                    <h3 style={styles.heading}>3. What we do NOT do</h3>
                    <p style={styles.paragraphSmall}>To be clear, we:</p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>
                            <strong>Do not sell</strong> your personal data
                        </li>
                        <li style={styles.listItem}>
                            <strong>Do not "share" or "rent"</strong> your personal data for advertising
                        </li>
                        <li style={styles.listItem}>
                            <strong>Do not use</strong> your email or name for non-Local Chapter marketing or unrelated promotions
                        </li>
                        <li style={styles.listItem}>
                            <strong>Do not use</strong> your personal data for purposes unrelated to Local Chapter transactions
                        </li>
                    </ul>

                    <h3 style={styles.heading}>4. How we collect your data</h3>
                    <p style={styles.paragraphSmall}>We collect your personal data when you:</p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>Sign up as a member (online or in-store)</li>
                        <li style={styles.listItem}>Submit dish ratings/feedback as a logged-in member</li>
                        <li style={styles.listItem}>Contact us for support in relation to your membership or transactions</li>
                    </ul>

                    <h3 style={styles.heading}>5. Disclosure of personal data (only when necessary)</h3>
                    <p style={styles.paragraphSmall}>
                        We keep access to your data restricted. We may disclose your data only where needed to operate Local
                        Chapter, for example:
                    </p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>
                            To trusted service providers that support essential systems (e.g., membership/loyalty platform,
                            hosting, communications), <strong>only to the extent necessary</strong> and under
                            confidentiality/security obligations
                        </li>
                        <li style={styles.listItem}>To public authorities where required by law</li>
                    </ul>

                    <h3 style={styles.heading}>6. Protection of personal data</h3>
                    <p style={styles.paragraph}>
                        We implement reasonable administrative and technical safeguards to protect your personal data against
                        unauthorised access, collection, use, disclosure, copying, modification, disposal, or similar risks.
                    </p>

                    <h3 style={styles.heading}>7. Retention of personal data</h3>
                    <p style={styles.paragraph}>
                        We retain your personal data only for as long as necessary to manage your membership, loyalty points, and
                        transaction-related records, or as required by law. When it is no longer needed, we will delete or
                        anonymise it.
                    </p>

                    <h3 style={styles.heading}>8. Access, correction, and withdrawal of consent</h3>
                    <p style={styles.paragraphSmall}>You may request:</p>
                    <ul style={styles.list}>
                        <li style={styles.listItem}>
                            <strong>Access</strong> to the personal data we hold about you
                        </li>
                        <li style={styles.listItem}>
                            <strong>Correction</strong> of inaccurate or incomplete data
                        </li>
                        <li style={styles.listItem}>
                            <strong>Withdrawal of consent</strong> (note: withdrawing consent may prevent us from maintaining your
                            membership and providing loyalty points, as we cannot link transactions to your account)
                        </li>
                    </ul>

                    <h3 style={styles.heading}>9. Updates to this Privacy Policy</h3>
                    <p style={styles.paragraph}>
                        We may update this Privacy Policy from time to time. The updated version will be posted on this page with
                        a revised "Last updated" date.
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={toggle}>
                    I Understand
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default PrivacyPolicyModal

const styles = {
    modalBody: {
        maxHeight: '70vh',
        overflowY: 'auto',
    },
    container: {
        lineHeight: '1.8',
    },
    paragraph: {
        marginBottom: '1.5rem',
    },
    paragraphSmall: {
        marginBottom: '1rem',
    },
    paragraphMeta: {
        marginBottom: '1.5rem',
        color: '#666',
    },
    heading: {
        marginTop: '2rem',
        marginBottom: '1rem',
    },
    list: {
        marginBottom: '1.5rem',
        paddingLeft: '1.5rem',
    },
    listSmall: {
        marginBottom: '1rem',
        paddingLeft: '1.5rem',
    },
    listItem: {
        marginBottom: '0.5rem',
    },
}
